import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, FolderOpen, Trash2, Truck } from "lucide-react";
import { api, apiError } from "../lib/api";
import { fmtDate, fmtDay } from "../lib/model";
import { AppHeader, AppFooter, NewInspectionButton } from "../components/layout/Layout";
import { ClassBadge } from "../components/common/Field";

const BANNER = "https://images.unsplash.com/photo-1574757974346-45bae947d89a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

function Stat({ label, value, accent, testId }) {
  return (
    <div className="bg-[#0B1520] border border-[#1E2F45] rounded-sm p-4 hover:border-[#FFD500]/40 transition-colors fade-up" data-testid={testId}>
      <div className="caption-label">{label}</div>
      <div className={`font-display font-black text-4xl mt-2 ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const load = () => api.get("/inspections").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (items || []).filter((i) => {
    const v = i.vehicle || {};
    return [v.placa, v.cliente, v.numero_ordem_servico, v.marca, v.modelo, i.owner_name].join(" ").toLowerCase().includes(q.toLowerCase());
  }), [items, q]);

  const count = (c) => (items || []).filter((i) => i.analysis?.classificacao_final === c).length;

  const remove = async (id) => {
    if (!window.confirm("Excluir esta inspeção e todas as fotos? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/inspections/${id}`);
      toast.success("Inspeção excluída");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 max-w-[1500px] w-full mx-auto">
        <div className="relative overflow-hidden rounded-sm border border-[#1E2F45] mb-8">
          <img src={BANNER} alt="Inspeção de chassi" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050B10] via-[#050B10]/85 to-transparent" />
          <div className="relative p-6 sm:p-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="fade-up">
              <div className="font-mono text-xs tracking-[0.3em] text-[#FFD500]">PAINEL DE INSPEÇÕES</div>
              <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl text-white mt-2 leading-none">Relatório digital<br />de alinhamento</h1>
              <p className="text-slate-300 mt-3 max-w-lg text-sm sm:text-base">Crie uma inspeção, registre coordenadas e gere o mapa de desalinhamento e o laudo técnico.</p>
            </div>
            <NewInspectionButton className="h-12 px-6 text-lg" />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat label="Inspeções" value={items ? items.length : "—"} testId="stat-total" />
          <Stat label="Aprovados" value={count("APROVADO") + count("APROVADO COM OBSERVAÇÕES")} accent="text-emerald-400" testId="stat-aprovados" />
          <Stat label="Necessita correção" value={count("NECESSITA CORREÇÃO")} accent="text-orange-400" testId="stat-correcao" />
          <Stat label="Reprovados" value={count("REPROVADO PARA OS PARÂMETROS INFORMADOS")} accent="text-red-500" testId="stat-reprovados" />
        </div>

        <div className="bg-[#0B1520] border border-[#1E2F45] rounded-sm">
          <div className="flex flex-wrap items-center gap-3 justify-between p-4 border-b border-[#1E2F45]">
            <h2 className="font-display font-bold uppercase tracking-wider text-xl text-white">Inspeções registradas</h2>
            <div className="relative w-full sm:w-80">
              <Search size={15} className="absolute left-3 top-2.5 text-slate-500" />
              <input data-testid="dashboard-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar placa, cliente, OS..."
                className="w-full h-9 pl-9 pr-3 bg-[#050B10] border border-[#1E2F45] rounded-sm text-sm focus:outline-none focus:border-[#FFD500]" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inspections-table">
              <thead>
                <tr className="text-left caption-label border-b border-[#1E2F45]">
                  {["OS", "Placa", "Cliente", "Veículo", "Data", "Executor", "Resultado", "Atualizado", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => {
                  const v = i.vehicle || {};
                  return (
                    <tr key={i.id} data-testid={`inspection-row-${i.id}`} className="border-b border-[#1E2F45]/60 hover:bg-[#111E2E] cursor-pointer transition-colors" onClick={() => nav(`/inspecao/${i.id}`)}>
                      <td className="px-4 py-3 font-mono text-slate-300">{v.numero_ordem_servico || "—"}</td>
                      <td className="px-4 py-3 font-mono font-bold text-[#FFD500]">{v.placa || "SEM PLACA"}</td>
                      <td className="px-4 py-3 text-white">{v.cliente || "—"}</td>
                      <td className="px-4 py-3 text-slate-300">{[v.marca, v.modelo].filter(Boolean).join(" ") || "—"}<div className="text-xs text-slate-500">{v.tipo_veiculo}</div></td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">{fmtDay(v.data_inspecao)}</td>
                      <td className="px-4 py-3 text-slate-300">{v.executor || i.owner_name}</td>
                      <td className="px-4 py-3"><ClassBadge value={i.analysis?.classificacao_final} /></td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtDate(i.updated_at)}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end">
                          <button type="button" data-testid={`open-inspection-${i.id}`} onClick={() => nav(`/inspecao/${i.id}`)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-[#1E2F45] text-slate-300 hover:text-[#FFD500] hover:border-[#FFD500]/60"><FolderOpen size={15} /></button>
                          <button type="button" data-testid={`delete-inspection-${i.id}`} onClick={() => remove(i.id)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-[#1E2F45] text-slate-400 hover:text-white"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {items && !filtered.length && (
              <div className="py-16 flex flex-col items-center text-center gap-3" data-testid="inspections-empty">
                <Truck size={40} className="text-[#1E2F45]" />
                <div className="font-display font-bold uppercase text-xl text-slate-300">Nenhuma inspeção encontrada</div>
                <p className="text-sm text-slate-500">Clique em NOVA INSPEÇÃO para iniciar o primeiro relatório.</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
